'use client';

import styles from './MeasurementPlan.module.scss';
import { ArrowDown, Bot, Brain, Shield, Target, UserCheck } from 'lucide-react';
import type { MeasurementPlan, Metric } from '@/src/lib/measurementPlan/schema';

interface PreviewCardProps {
  plan: MeasurementPlan;
  isEn: boolean;
  onUnlock: () => void;
}

interface MetricPair {
  gain: Metric;
  guardrail: Metric | null;
}

function buildPairs(metrics: Metric[]): MetricPair[] {
  const used = new Set<string>();
  const pairs: MetricPair[] = [];

  metrics.forEach((metric) => {
    if (metric.category !== 'business' || used.has(metric.name)) {
      return;
    }

    const guardrail = metrics.find(
      (m) => m.name === metric.pairedGuardrail || m.pairedGuardrail === metric.name
    );

    pairs.push({ gain: metric, guardrail: guardrail || null });
    used.add(metric.name);
    if (guardrail) {
      used.add(guardrail.name);
    }
  });

  return pairs.slice(0, 3);
}

function findMainRisk(
  plan: MeasurementPlan
): { type: 'technical' | 'business' | 'ai'; text: string } | null {
  if (plan.aiEvaluation.isAiRelevant && plan.aiEvaluation.riskSummary) {
    return { type: 'ai', text: plan.aiEvaluation.riskSummary };
  }
  if (plan.technicalDebt.length > 0) {
    return { type: 'technical', text: plan.technicalDebt[0].description };
  }
  if (plan.businessDebt.length > 0) {
    return { type: 'business', text: plan.businessDebt[0].description };
  }
  return null;
}

export function PreviewCard({ plan, isEn, onUnlock }: PreviewCardProps) {
  const labels = {
    eyebrow: isEn ? 'Free preview' : 'Aperçu gratuit',
    objectiveLabel: isEn ? 'Business objective' : 'Objectif métier',
    northStarLabel: isEn ? 'North Star metric' : 'Métrique North Star',
    metricsLabel: isEn ? 'Paired metrics' : 'Métriques appariées',
    mainRiskLabel: isEn ? 'Main risk' : 'Risque principal',
    aiRiskLabel: isEn ? 'AI risk' : 'Risque IA',
    unlockCta: isEn ? 'Get my full Confluence plan' : 'Recevoir mon plan Confluence complet',
    trustPrivacy: isEn ? 'Privacy-first' : 'Confidentialité',
    trustHuman: isEn ? 'Human expert review' : 'Revue humaine',
    trustNoLock: isEn ? 'No lock-in' : 'Sans engagement',
  };

  const pairs = buildPairs(plan.metrics);
  const mainRisk = findMainRisk(plan);
  const showAiRisk = plan.aiEvaluation.isAiRelevant;

  return (
    <div className={styles.previewCard} data-testid="preview-card">
      <div className={styles.previewHeader}>
        <span className={styles.previewEyebrow}>
          <Target size={14} />
          {labels.eyebrow}
        </span>
        <h2 className={styles.previewTitle}>{plan.title}</h2>
      </div>

      <div>
        <span className={styles.previewSectionLabel}>{labels.objectiveLabel}</span>
        <p className={styles.previewObjective}>{plan.businessObjective}</p>
      </div>

      <div>
        <span className={styles.previewSectionLabel}>{labels.northStarLabel}</span>
        <div className={styles.northStarBadge}>
          <Target size={16} />
          {plan.northStarMetric}
        </div>
      </div>

      <div>
        <span className={styles.previewSectionLabel}>{labels.metricsLabel}</span>
        <div className={styles.metricsGrid}>
          {pairs.map((pair, idx) => (
            <div key={idx} className={styles.metricPairCard}>
              <div className={styles.metricPairHeader}>
                {isEn ? 'Gain' : 'Gain'} #{idx + 1}
              </div>
              <div className={styles.metricPairGain}>
                <span className={styles.metricPairName}>{pair.gain.name}</span>
                <span className={styles.metricPairReason}>{pair.gain.reason}</span>
              </div>
              <div className={styles.metricPairArrow}>
                <ArrowDown size={16} />
              </div>
              <div className={styles.metricPairGuardrail}>
                {pair.guardrail ? (
                  <>
                    <span className={styles.metricPairGuardrailName}>{pair.guardrail.name}</span>
                    <span className={styles.metricPairGuardrailReason}>
                      {pair.guardrail.reason}
                    </span>
                  </>
                ) : (
                  <span className={styles.metricPairGuardrailName}>
                    {isEn ? 'Guardrail to define' : 'Garde-fou à définir'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.risksRow}>
        {mainRisk && (
          <div
            className={`${styles.riskCard} ${
              mainRisk.type === 'technical'
                ? styles.riskCardTechnical
                : mainRisk.type === 'ai'
                  ? styles.riskCardAi
                  : styles.riskCardBusiness
            }`}
          >
            <span className={styles.riskLabel}>
              {mainRisk.type === 'technical' ? (
                <>
                  <Brain size={14} />
                  {labels.mainRiskLabel}
                </>
              ) : mainRisk.type === 'ai' ? (
                <>
                  <Bot size={14} />
                  {labels.aiRiskLabel}
                </>
              ) : (
                <>
                  <Shield size={14} />
                  {labels.mainRiskLabel}
                </>
              )}
            </span>
            <p className={styles.riskText}>{mainRisk.text}</p>
          </div>
        )}

        {showAiRisk && mainRisk?.type !== 'ai' && (
          <div className={`${styles.riskCard} ${styles.riskCardAi}`}>
            <span className={styles.riskLabel}>
              <Bot size={14} />
              {labels.aiRiskLabel}
            </span>
            <p className={styles.riskText}>{plan.aiEvaluation.riskSummary}</p>
          </div>
        )}
      </div>

      <div className={styles.trustBadges}>
        <span className={styles.trustBadge}>
          <Shield size={14} />
          {labels.trustPrivacy}
        </span>
        <span className={styles.trustBadge}>
          <UserCheck size={14} />
          {labels.trustHuman}
        </span>
        <span className={styles.trustBadge}>
          <Target size={14} />
          {labels.trustNoLock}
        </span>
      </div>

      <button type="button" className={styles.unlockCta} onClick={onUnlock}>
        {labels.unlockCta}
      </button>
    </div>
  );
}
